/**
 * Release 2.2B — Deals list.
 * - Removes the inline modal; "New deal" navigates to /app/deals/new (Wizard).
 * - Shows real Buyer/Seller/Product/Total/Currency/Stage/Status/Next Action/Updated.
 * - Search by title/reference/company/product; filter by status/stage; sort default recent-update.
 * - Includes an "Include archived" toggle.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { Plus, ArrowUpRight, Search } from "lucide-react";
import {
  dealTypeLabelKey, dealStageLabelKey, dealStatusLabelKey,
  DEAL_STATUSES_NEW, DEAL_STAGES_NEW, summariseDealForList,
} from "@/lib/dealsMeta";

export default function DealsPage() {
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("updated");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);

  // Backward-compat: /app/deals?new=1 still opens the wizard.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("new") === "1") nav("/app/deals/new", { replace: true });
  }, [location.search, nav]);

  const load = async () => {
    setLoading(true);
    try { setDeals(await api.listDeals()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredSorted = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = deals;
    if (!includeArchived) list = list.filter((d) => d.status !== "archived");
    if (statusFilter !== "all") list = list.filter((d) => d.status === statusFilter);
    if (stageFilter !== "all") list = list.filter((d) => d.stage === stageFilter);
    if (s) {
      list = list.filter((d) => {
        const summ = summariseDealForList(d);
        return (
          d.title?.toLowerCase().includes(s) ||
          (d.reference || "").toLowerCase().includes(s) ||
          (d.counterparty || "").toLowerCase().includes(s) ||
          summ.buyer.toLowerCase().includes(s) ||
          summ.seller.toLowerCase().includes(s) ||
          summ.product.toLowerCase().includes(s)
        );
      });
    }
    const sorted = [...list];
    if (sortKey === "updated") sorted.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    else if (sortKey === "created") sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    else if (sortKey === "title") sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (sortKey === "reference") sorted.sort((a, b) => (a.reference || "").localeCompare(b.reference || ""));
    return sorted;
  }, [deals, q, sortKey, statusFilter, stageFilter, includeArchived]);

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="kicker mb-3">— {t("d_deals_kicker")}</div>
          <h1 className="font-serif text-4xl">{t("d_deals_title")}</h1>
        </div>
        <Link
          to="/app/deals/new"
          data-testid={TESTIDS.dealsNewBtn}
          className="btn-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <Plus size={14} /> {t("dl_deals_new_btn")}
        </Link>
      </div>

      {loading ? (
        <div className="text-[var(--td-text-2)] font-mono text-xs">{t("d_deals_loading")}</div>
      ) : deals.length === 0 ? (
        <div className="card-surface p-16 text-center text-[var(--td-text-2)]">
          <div className="font-serif text-xl mb-2">{t("d_deals_empty")}</div>
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[var(--td-text-3)]" />
              <input data-testid={TESTIDS.dealsSearch} placeholder={t("dl_search_ph")}
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="td-input pl-9" />
            </div>
            <select data-testid="deals-status-filter" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label={t("dl_filter_status")}
                    className="td-input font-mono text-xs uppercase tracking-widest md:w-56">
              <option value="all">{t("dl_filter_status")}: {t("dl_filter_all")}</option>
              {DEAL_STATUSES_NEW.map((s) => <option key={s} value={s}>{t(`dst_${s}`)}</option>)}
            </select>
            <select data-testid="deals-stage-filter" value={stageFilter}
                    onChange={(e) => setStageFilter(e.target.value)}
                    aria-label={t("dl_filter_stage")}
                    className="td-input font-mono text-xs uppercase tracking-widest md:w-56">
              <option value="all">{t("dl_filter_stage")}: {t("dl_filter_all")}</option>
              {DEAL_STAGES_NEW.map((s) => <option key={s} value={s}>{t(`ds_${s}`)}</option>)}
            </select>
            <select data-testid={TESTIDS.dealsSort} value={sortKey}
                    onChange={(e) => setSortKey(e.target.value)}
                    className="td-input font-mono text-xs uppercase tracking-widest md:w-56">
              <option value="updated">{t("dl_sort_updated")}</option>
              <option value="created">{t("dl_sort_created")}</option>
              <option value="title">{t("dl_sort_title")}</option>
              <option value="reference">{t("dl_sort_reference")}</option>
            </select>
            <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--td-text-2)]">
              <input type="checkbox" checked={includeArchived}
                     data-testid="deals-include-archived"
                     onChange={(e) => setIncludeArchived(e.target.checked)} />
              {t("dl_include_archived")}
            </label>
          </div>

          {filteredSorted.length === 0 ? (
            <div className="card-surface p-12 text-center text-[var(--td-text-2)]">
              <div className="font-serif text-lg">{t("dl_no_match")}</div>
            </div>
          ) : (
            <>
            <div className="md:hidden space-y-3" data-testid={`${TESTIDS.dealsTable}-mobile`}>
              {filteredSorted.map((d) => {
                const summ = summariseDealForList(d);
                const stageKey = dealStageLabelKey(d.stage);
                const statusKey = dealStatusLabelKey(d.status);
                return (
                  <article key={d.id} data-testid={TESTIDS.dealRow}
                    className={`card-surface p-4 ${d.status === "archived" ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)] mb-1">{d.reference || "—"}</div>
                        <Link to={`/app/deals/${d.id}`} className="font-serif text-xl leading-tight text-[var(--td-text)] inline-flex items-start gap-1.5">
                          <span className="break-words">{d.title}</span><ArrowUpRight size={14} className="shrink-0 mt-1 opacity-50" />
                        </Link>
                      </div>
                      <span className={`shrink-0 px-2 py-1 border font-mono text-[9px] uppercase tracking-wider ${statusColor(d.status)}`}>
                        {statusKey ? t(statusKey) : d.status}
                      </span>
                    </div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                      <div><dt className="font-mono text-[9px] uppercase tracking-wider text-[var(--td-text-3)]">{t("d_col_stage")}</dt><dd className="mt-1 text-[var(--td-text-2)]">{stageKey ? t(stageKey) : d.stage}</dd></div>
                      <div><dt className="font-mono text-[9px] uppercase tracking-wider text-[var(--td-text-3)]">{t("dl_col_total")}</dt><dd className="mt-1 text-[var(--td-text)]">{summ.total != null ? `${summ.currency || ""} ${summ.total.toLocaleString()}`.trim() : (d.value || "—")}</dd></div>
                      <div><dt className="font-mono text-[9px] uppercase tracking-wider text-[var(--td-text-3)]">{t("dl_col_buyer")}</dt><dd className="mt-1 text-[var(--td-text-2)] break-words">{summ.buyer || "—"}</dd></div>
                      <div><dt className="font-mono text-[9px] uppercase tracking-wider text-[var(--td-text-3)]">{t("dl_col_seller")}</dt><dd className="mt-1 text-[var(--td-text-2)] break-words">{summ.seller || "—"}</dd></div>
                    </dl>
                    {summ.nextActionTitle && <div className="mt-3 pt-3 border-t border-[var(--td-border)] text-xs text-[var(--td-text-2)] break-words">{summ.nextActionTitle}</div>}
                  </article>
                );
              })}
            </div>
            <div className="hidden md:block card-surface overflow-x-auto" data-testid={TESTIDS.dealsTable}>
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="hairline-bottom">
                    {[t("dl_col_reference"), t("d_col_title"), t("dl_col_buyer"), t("dl_col_seller"),
                      t("dl_col_product"), t("dl_col_total"), t("d_col_stage"), t("dl_col_status"),
                      t("dl_col_next_action"), t("d_col_updated")].map((h, i) => (
                      <th key={i} className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-2)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((d) => {
                    const summ = summariseDealForList(d);
                    const stageKey = dealStageLabelKey(d.stage);
                    const statusKey = dealStatusLabelKey(d.status);
                    const typeKey = dealTypeLabelKey(d.deal_type);
                    return (
                      <tr key={d.id} data-testid={TESTIDS.dealRow}
                          className={`hairline-bottom hover:bg-[var(--td-surface-2)] transition-colors ${d.status === "archived" ? "opacity-60" : ""}`}>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--td-text-2)] whitespace-nowrap">{d.reference || "—"}</td>
                        <td className="px-4 py-3">
                          <Link to={`/app/deals/${d.id}`}
                                className="font-serif text-base hover:text-[var(--td-gold)] transition-colors inline-flex items-center gap-1.5">
                            {d.title}
                            <ArrowUpRight size={13} className="opacity-40" />
                          </Link>
                          <div className="text-[10px] font-mono uppercase text-[var(--td-text-3)] mt-0.5">
                            {typeKey ? t(typeKey) : d.deal_type}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[var(--td-text-2)]">{summ.buyer || "—"}</td>
                        <td className="px-4 py-3 text-[var(--td-text-2)]">{summ.seller || "—"}</td>
                        <td className="px-4 py-3 text-[var(--td-text-2)]">{summ.product || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--td-text)] whitespace-nowrap">
                          {summ.total != null ? `${summ.currency || ""} ${summ.total.toLocaleString()}`.trim() : (d.value || "—")}
                        </td>
                        <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-2)]">
                          {stageKey ? t(stageKey) : d.stage}
                        </td>
                        <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest">
                          <span className={`inline-block px-2 py-1 border ${statusColor(d.status)}`}>{statusKey ? t(statusKey) : d.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[var(--td-text-2)] max-w-[220px] truncate">{summ.nextActionTitle || "—"}</td>
                        <td className="px-4 py-3 font-mono text-[10px] text-[var(--td-text-3)] whitespace-nowrap">{new Date(d.updated_at).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function statusColor(status) {
  const map = {
    draft:            "border-[var(--td-border)] text-[var(--td-text-3)]",
    active:           "border-[var(--td-gold-muted)] text-[var(--td-gold)]",
    on_hold:          "border-amber-500/40 text-amber-400/90",
    needs_attention:  "border-red-500/40 text-red-400/90",
    completed:        "border-emerald-500/40 text-emerald-400/90",
    archived:         "border-[var(--td-border)] text-[var(--td-text-3)]",
    closed:           "border-[var(--td-border)] text-[var(--td-text-3)]",
  };
  return map[status] || "border-[var(--td-border)] text-[var(--td-text-3)]";
}
